/**
 * SoleadLab 2.0 - Manual Orthophoto Positioning Markers
 *
 * When a manual (non-GeoTIFF) orthophoto is clicked on the map, this hook creates
 * draggable Mapbox markers:
 * - 4 corner markers (orange diamonds) for free-form corner adjustment
 * - 4 edge midpoint markers (teal circles) for proportional scaling
 * - 1 center marker (orange circle) for moving
 * - 1 rotation handle (green circle with dashed line) for rotating
 *
 * Clicking elsewhere on the map hides the markers.
 * All changes update the Mapbox image source in real-time via setCoordinates().
 */

import { useEffect, useRef } from 'react';
import { useIsReadOnlyViewer } from '../../hooks/useShareViewer';
import mapboxgl from 'mapbox-gl';
import { useMapStore, useProjectStore } from '../../stores';

// ─── Helpers ────────────────────────────────────────────────────────────────

function getCenter(coords: number[][]): [number, number] {
  const lngs = coords.map((c) => c[0]);
  const lats = coords.map((c) => c[1]);
  return [
    (Math.min(...lngs) + Math.max(...lngs)) / 2,
    (Math.min(...lats) + Math.max(...lats)) / 2,
  ];
}

function getMidpoint(p1: number[], p2: number[]): [number, number] {
  return [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
}

/** Ray-casting point-in-polygon test */
function pointInPolygon(px: number, py: number, polygon: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1];
    const xj = polygon[j][0], yj = polygon[j][1];
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

// ─── Marker element factories ───────────────────────────────────────────────

function createCornerElement(cursor: string): HTMLDivElement {
  const el = document.createElement('div');
  el.style.cssText = `width:12px; height:12px; background:#f59e0b; border:2px solid #fff; cursor:${cursor}; box-shadow:0 2px 4px rgba(0,0,0,0.3); transform:rotate(45deg);`;
  el.title = 'Drag to adjust corner';
  return el;
}

function createScaleElement(): HTMLDivElement {
  const el = document.createElement('div');
  el.style.cssText =
    'width:14px; height:14px; background:#13d6c6; border:2px solid #fff; border-radius:50%; cursor:pointer; box-shadow:0 2px 4px rgba(0,0,0,0.3);';
  el.title = 'Drag to scale (keeps proportions)';
  return el;
}

function createCenterElement(): HTMLDivElement {
  const el = document.createElement('div');
  el.style.cssText =
    'width:22px; height:22px; background:#f59e0b; border:3px solid #fff; border-radius:50%; cursor:move; box-shadow:0 2px 6px rgba(0,0,0,0.4); display:flex; align-items:center; justify-content:center;';
  el.innerHTML = '<div style="width:8px; height:8px; background:#fff; border-radius:50%;"></div>';
  return el;
}

function createRotationElement(): HTMLDivElement {
  const el = document.createElement('div');
  el.style.cssText =
    'width:20px; height:20px; background:#10b981; border:3px solid #fff; border-radius:50%; cursor:grab; box-shadow:0 2px 6px rgba(0,0,0,0.4); display:flex; align-items:center; justify-content:center; font-size:12px; color:#fff;';
  el.textContent = '↻';
  el.title = 'Drag to rotate';
  return el;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export default function useManualOrthoMarkers() {
  const activeOrthophotoId = useMapStore((s) => s.activeOrthophotoId);
  const orthoEditMode = useMapStore((s) => s.orthoEditMode);
  const isMapReady = useMapStore((s) => s.isMapReady);
  // F-02E-2C: share viewers must never enter manual-ortho edit mode.
  // Mirrors the overlay-markers gate — skip both the click-select
  // effect and the marker-render effect.
  const isReadOnly = useIsReadOnlyViewer();

  // Refs for cleanup
  const cornerMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const scaleMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const centerMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const rotationMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const rotationLineRef = useRef<boolean>(false);
  // Track if a marker is being dragged — suppress map click during drag
  const isDraggingRef = useRef(false);

  // ── Effect 1: Map click handler — select/deselect manual orthophotos ────
  useEffect(() => {
    if (!isMapReady) return;
    if (isReadOnly) return;
    const map = useMapStore.getState().map;
    if (!map) return;

    const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
      if (useMapStore.getState().overlayEditMode) return;
      // Don't interfere if a marker is being dragged
      if (isDraggingRef.current) return;

      const clickLng = e.lngLat.lng;
      const clickLat = e.lngLat.lat;

      // Check all manual orthophotos — find which one (if any) was clicked
      const orthos = useProjectStore.getState().data.orthophotos;
      let clickedId: string | null = null;

      for (const ortho of orthos) {
        if (!ortho.visible) continue;
        if (pointInPolygon(clickLng, clickLat, ortho.coordinates)) {
          clickedId = ortho.id;
          break;
        }
      }

      const currentActive = useMapStore.getState().activeOrthophotoId;

      if (!clickedId && currentActive) {
        // Clicked outside any orthophoto — deactivate selection and edit mode
        useMapStore.getState().setActiveOrthophoto(null);
      } else if (clickedId && currentActive !== clickedId) {
        // Clicked on a different orthophoto — select it (show controls) but don't enter edit mode
        useMapStore.getState().setActiveOrthophoto(clickedId);
      }
    };

    map.on('click', handleMapClick);
    return () => { map.off('click', handleMapClick); };
  }, [isMapReady, isReadOnly]);

  // ── Effect 2: Create/remove markers based on activeOrthophotoId ─────────
  useEffect(() => {
    const map = useMapStore.getState().map;
    if (!map) return;
    // F-02E-2C: never render edit-handle markers for share viewers.
    if (isReadOnly) return;

    // ── Clear previous markers ──────────────────────────────────────────
    const cleanup = () => {
      cornerMarkersRef.current.forEach((m) => { try { m.remove(); } catch (_e) { /* */ } });
      cornerMarkersRef.current = [];

      scaleMarkersRef.current.forEach((m) => { try { m.remove(); } catch (_e) { /* */ } });
      scaleMarkersRef.current = [];

      if (centerMarkerRef.current) {
        try { centerMarkerRef.current.remove(); } catch (_e) { /* */ }
        centerMarkerRef.current = null;
      }

      if (rotationMarkerRef.current) {
        try { rotationMarkerRef.current.remove(); } catch (_e) { /* */ }
        rotationMarkerRef.current = null;
      }

      if (rotationLineRef.current) {
        try { if (map.getLayer('rotation-line-layer')) map.removeLayer('rotation-line-layer'); } catch (_e) { /* */ }
        try { if (map.getSource('rotation-line-source')) map.removeSource('rotation-line-source'); } catch (_e) { /* */ }
        rotationLineRef.current = false;
      }

    };

    cleanup();

    // ── Only show markers when orthoEditMode is active ────────────────
    if (!activeOrthophotoId || !orthoEditMode) return cleanup;

    const ortho = useProjectStore.getState().data.orthophotos.find((o) => o.id === activeOrthophotoId);
    if (!ortho) return cleanup;

    // Local mutable copy of coordinates (updated during drag, flushed to store on dragend)
    const coords = ortho.coordinates.map((c) => [...c]) as [number, number][];

    // Helper: push coordinate changes to the Mapbox source in real-time.
    // MapCanvas's ortho render effect swaps the ortho under edit from raster
    // tiles to its draggable preview image (and restores tiles on exit), so by
    // the time drags land here the source accepts setCoordinates. The guard
    // covers the brief async window while MapCanvas is still swapping.
    // tileStatus is deliberately NOT touched: wiping it made the tile-status
    // poller's self-heal re-flip it to 'ready' mid-edit, re-baking the tile
    // source under the handles (the "blind drag").
    const syncToMapSource = () => {
      const sourceId = `ortho-source-${activeOrthophotoId}`;
      const source = map.getSource(sourceId);
      if (source && typeof (source as any).setCoordinates === 'function') {
        (source as any).setCoordinates(coords);
      }
    };

    // Helper: reposition all markers to current coords
    const edges = [[0, 1], [1, 2], [2, 3], [3, 0]];
    const repositionAll = () => {
      for (let i = 0; i < cornerMarkersRef.current.length; i++) {
        cornerMarkersRef.current[i].setLngLat(coords[i] as [number, number]);
      }
      for (let i = 0; i < scaleMarkersRef.current.length; i++) {
        const mid = getMidpoint(coords[edges[i][0]], coords[edges[i][1]]);
        scaleMarkersRef.current[i].setLngLat(mid);
      }
      if (centerMarkerRef.current) {
        centerMarkerRef.current.setLngLat(getCenter(coords));
      }
      updateRotationHandlePosition();
    };

    const updateRotationHandlePosition = () => {
      const topMid = getMidpoint(coords[0], coords[1]);
      const dy = Math.abs(coords[0][1] - coords[3][1]);
      const dx = Math.abs(coords[0][0] - coords[3][0]);
      const height = Math.sqrt(dy * dy + dx * dx);
      const center = getCenter(coords);
      const dirX = topMid[0] - center[0];
      const dirY = topMid[1] - center[1];
      const dirLen = Math.sqrt(dirX * dirX + dirY * dirY);
      const handlePos: [number, number] = dirLen > 0
        ? [topMid[0] + (dirX / dirLen) * height * 0.3, topMid[1] + (dirY / dirLen) * height * 0.3]
        : [topMid[0], topMid[1] + height * 0.3];

      if (rotationMarkerRef.current) {
        rotationMarkerRef.current.setLngLat(handlePos);
      }

      const lineSource = map.getSource('rotation-line-source') as mapboxgl.GeoJSONSource | undefined;
      if (lineSource) {
        lineSource.setData({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [center, handlePos] },
          properties: {},
        });
      }
    };

    // Helper: flush coords to project store (on dragend)
    const flushToStore = () => {
      const typedCoords = coords as [[number, number], [number, number], [number, number], [number, number]];
      const lngs = coords.map((c) => c[0]);
      const lats = coords.map((c) => c[1]);
      const updates: Record<string, unknown> = {
        coordinates: typedCoords,
        bounds: {
          minLng: Math.min(...lngs),
          maxLng: Math.max(...lngs),
          minLat: Math.min(...lats),
          maxLat: Math.max(...lats),
        },
      };
      // Coordinates/bounds only — tileStatus stays 'ready' so the HD badge
      // survives the adjust; Regen HD bakes the new position.
      useProjectStore.getState().updateOrthophoto(activeOrthophotoId, updates);
    };

    // ═══ 1. CORNER MARKERS (4) — free-form individual corner adjustment ══
    const cornerCursors = ['nw-resize', 'ne-resize', 'se-resize', 'sw-resize'];

    for (let i = 0; i < 4; i++) {
      const el = createCornerElement(cornerCursors[i]);
      const marker = new mapboxgl.Marker({ element: el, draggable: true })
        .setLngLat(coords[i] as [number, number])
        .addTo(map);

      marker.on('dragstart', () => { isDraggingRef.current = true; });
      marker.on('drag', () => {
        const ll = marker.getLngLat();
        coords[i] = [ll.lng, ll.lat];
        repositionAll();
        syncToMapSource();
      });
      marker.on('dragend', () => {
        isDraggingRef.current = false;
        flushToStore();
      });

      cornerMarkersRef.current.push(marker);
    }

    // ═══ 2. EDGE MIDPOINT MARKERS (4) — proportional scale ═══════════════
    let scaleStart: {
      centerLng: number;
      centerLat: number;
      originalCoords: number[][];
      originalDist: number;
    } | null = null;

    for (let e = 0; e < 4; e++) {
      const mid = getMidpoint(coords[edges[e][0]], coords[edges[e][1]]);
      const el = createScaleElement();
      const marker = new mapboxgl.Marker({ element: el, draggable: true })
        .setLngLat(mid)
        .addTo(map);

      marker.on('dragstart', () => {
        isDraggingRef.current = true;
        const c = getCenter(coords);
        const edgeMid = getMidpoint(coords[edges[e][0]], coords[edges[e][1]]);
        const dx = edgeMid[0] - c[0];
        const dy = edgeMid[1] - c[1];
        scaleStart = {
          centerLng: c[0],
          centerLat: c[1],
          originalCoords: coords.map((pt) => [...pt]),
          originalDist: Math.sqrt(dx * dx + dy * dy),
        };
      });

      marker.on('drag', () => {
        if (!scaleStart || scaleStart.originalDist === 0) return;

        const ll = marker.getLngLat();
        const dx = ll.lng - scaleStart.centerLng;
        const dy = ll.lat - scaleStart.centerLat;
        const newDist = Math.sqrt(dx * dx + dy * dy);
        const scale = newDist / scaleStart.originalDist;

        for (let j = 0; j < 4; j++) {
          const ox = scaleStart.originalCoords[j][0] - scaleStart.centerLng;
          const oy = scaleStart.originalCoords[j][1] - scaleStart.centerLat;
          coords[j][0] = scaleStart.centerLng + ox * scale;
          coords[j][1] = scaleStart.centerLat + oy * scale;
        }

        repositionAll();
        syncToMapSource();
      });

      marker.on('dragend', () => {
        isDraggingRef.current = false;
        scaleStart = null;
        flushToStore();
      });

      scaleMarkersRef.current.push(marker);
    }

    // ═══ 3. CENTER MARKER (move) ════════════════════════════════════════
    const centerEl = createCenterElement();
    const centerPos = getCenter(coords);
    const centerMarker = new mapboxgl.Marker({ element: centerEl, draggable: true })
      .setLngLat(centerPos)
      .addTo(map);

    let lastCenter = { lng: centerPos[0], lat: centerPos[1] };

    centerMarker.on('dragstart', () => {
      isDraggingRef.current = true;
      const ll = centerMarker.getLngLat();
      lastCenter = { lng: ll.lng, lat: ll.lat };
    });

    centerMarker.on('drag', () => {
      const ll = centerMarker.getLngLat();
      const dLng = ll.lng - lastCenter.lng;
      const dLat = ll.lat - lastCenter.lat;

      for (let i = 0; i < 4; i++) {
        coords[i][0] += dLng;
        coords[i][1] += dLat;
      }
      lastCenter = { lng: ll.lng, lat: ll.lat };

      for (let i = 0; i < cornerMarkersRef.current.length; i++) {
        cornerMarkersRef.current[i].setLngLat(coords[i] as [number, number]);
      }
      for (let i = 0; i < scaleMarkersRef.current.length; i++) {
        const mid = getMidpoint(coords[edges[i][0]], coords[edges[i][1]]);
        scaleMarkersRef.current[i].setLngLat(mid);
      }
      updateRotationHandlePosition();
      syncToMapSource();
    });

    centerMarker.on('dragend', () => {
      isDraggingRef.current = false;
      flushToStore();
    });

    centerMarkerRef.current = centerMarker;

    // ═══ 4. ROTATION HANDLE ═════════════════════════════════════════════
    const topMid = getMidpoint(coords[0], coords[1]);
    const dy = Math.abs(coords[0][1] - coords[3][1]);
    const dx = Math.abs(coords[0][0] - coords[3][0]);
    const imgHeight = Math.sqrt(dy * dy + dx * dx);
    const center = getCenter(coords);
    const dirX = topMid[0] - center[0];
    const dirY = topMid[1] - center[1];
    const dirLen = Math.sqrt(dirX * dirX + dirY * dirY);
    const handlePos: [number, number] = dirLen > 0
      ? [topMid[0] + (dirX / dirLen) * imgHeight * 0.3, topMid[1] + (dirY / dirLen) * imgHeight * 0.3]
      : [topMid[0], topMid[1] + imgHeight * 0.3];

    map.addSource('rotation-line-source', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [center, handlePos] },
        properties: {},
      },
    });

    map.addLayer({
      id: 'rotation-line-layer',
      type: 'line',
      source: 'rotation-line-source',
      paint: {
        'line-color': '#f59e0b',
        'line-width': 2,
        'line-dasharray': [3, 3],
      },
    });
    rotationLineRef.current = true;

    const rotateEl = createRotationElement();
    const rotationMarker = new mapboxgl.Marker({ element: rotateEl, draggable: true })
      .setLngLat(handlePos)
      .addTo(map);

    let rotationStart: {
      centerLng: number;
      centerLat: number;
      startAngle: number;
      originalCoords: number[][];
      lngScale: number;
    } | null = null;

    rotationMarker.on('dragstart', () => {
      isDraggingRef.current = true;
      rotateEl.style.cursor = 'grabbing';
      const c = getCenter(coords);
      const hll = rotationMarker.getLngLat();
      const lngScale = Math.cos(c[1] * Math.PI / 180);
      rotationStart = {
        centerLng: c[0],
        centerLat: c[1],
        startAngle: Math.atan2(hll.lat - c[1], (hll.lng - c[0]) * lngScale),
        originalCoords: coords.map((c) => [...c]),
        lngScale,
      };
    });

    rotationMarker.on('drag', () => {
      if (!rotationStart) return;

      const hll = rotationMarker.getLngLat();
      const lngScale = rotationStart.lngScale;
      const currentAngle = Math.atan2(
        hll.lat - rotationStart.centerLat,
        (hll.lng - rotationStart.centerLng) * lngScale
      );
      const deltaAngle = currentAngle - rotationStart.startAngle;
      const cos = Math.cos(deltaAngle);
      const sin = Math.sin(deltaAngle);

      for (let i = 0; i < 4; i++) {
        const dx = (rotationStart.originalCoords[i][0] - rotationStart.centerLng) * lngScale;
        const dy = rotationStart.originalCoords[i][1] - rotationStart.centerLat;
        const rx = dx * cos - dy * sin;
        const ry = dx * sin + dy * cos;
        coords[i][0] = rotationStart.centerLng + rx / lngScale;
        coords[i][1] = rotationStart.centerLat + ry;
      }

      for (let i = 0; i < cornerMarkersRef.current.length; i++) {
        cornerMarkersRef.current[i].setLngLat(coords[i] as [number, number]);
      }
      for (let i = 0; i < scaleMarkersRef.current.length; i++) {
        const mid = getMidpoint(coords[edges[i][0]], coords[edges[i][1]]);
        scaleMarkersRef.current[i].setLngLat(mid);
      }
      if (centerMarkerRef.current) {
        centerMarkerRef.current.setLngLat(getCenter(coords));
      }
      const lineSource = map.getSource('rotation-line-source') as mapboxgl.GeoJSONSource | undefined;
      if (lineSource) {
        lineSource.setData({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [getCenter(coords), [hll.lng, hll.lat]] },
          properties: {},
        });
      }

      syncToMapSource();
    });

    rotationMarker.on('dragend', () => {
      isDraggingRef.current = false;
      rotateEl.style.cursor = 'grab';
      rotationStart = null;
      flushToStore();
    });

    rotationMarkerRef.current = rotationMarker;

    return cleanup;
  }, [activeOrthophotoId, orthoEditMode, isReadOnly]);
}
